/**
 * 需纠偏底图：将 WGS84 MVT 解码为 GeoJSON，再走 toMapGeometry（与点/线同一套适配器）。
 */
import vt from '@mapbox/vector-tile';
import Pbf from 'pbf';
import { toMapGeometry } from './dashboard/basemapCoord';

const VectorTile = vt.VectorTile || vt;

/** 与 mapLayerOverlay.normalizeClipBounds 同语义，避免循环依赖 */
function normalizeClipBounds(clip) {
  if (!clip) return null;
  if (Array.isArray(clip) && clip.length >= 4) {
    const [west, south, east, north] = clip.map(Number);
    if (![west, south, east, north].every(Number.isFinite)) return null;
    if (!(east > west && north > south)) return null;
    return { west, south, east, north };
  }
  const west = Number(clip.west);
  const south = Number(clip.south);
  const east = Number(clip.east);
  const north = Number(clip.north);
  if (![west, south, east, north].every(Number.isFinite)) return null;
  if (!(east > west && north > south)) return null;
  return { west, south, east, north };
}

function lngLatToTile(lng, lat, z) {
  const n = 2 ** z;
  const x = Math.floor(((lng + 180) / 360) * n);
  const latRad = (lat * Math.PI) / 180;
  const y = Math.floor(
    ((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2) * n
  );
  return {
    x: Math.min(n - 1, Math.max(0, x)),
    y: Math.min(n - 1, Math.max(0, y))
  };
}

/** 覆盖 WGS84 bbox 的瓦片列表（含边界） */
export function tilesForBbox(bbox, z) {
  const b = normalizeClipBounds(bbox);
  if (!b) return [];
  const zN = Math.max(0, Math.min(22, Number(z) || 0));
  const n = 2 ** zN;
  const a = lngLatToTile(b.west, b.north, zN);
  const c = lngLatToTile(b.east, b.south, zN);
  const x0 = Math.max(0, Math.min(a.x, c.x));
  const x1 = Math.min(n - 1, Math.max(a.x, c.x));
  const y0 = Math.max(0, Math.min(a.y, c.y));
  const y1 = Math.min(n - 1, Math.max(a.y, c.y));
  const out = [];
  for (let x = x0; x <= x1; x += 1) {
    for (let y = y0; y <= y1; y += 1) {
      out.push({ z: zN, x, y });
    }
  }
  return out;
}

/** 在 min~max zoom 内选瓦片数不超过 limit 的最高级别 */
export function pickTilesForBbox(bbox, minzoom, maxzoom, limit = 48) {
  const minZ = Number.isFinite(Number(minzoom)) ? Number(minzoom) : 10;
  const maxZ = Number.isFinite(Number(maxzoom)) ? Number(maxzoom) : 16;
  const prefer = Math.max(minZ, Math.min(maxZ, 13));
  for (let z = prefer; z >= minZ; z -= 1) {
    const tiles = tilesForBbox(bbox, z);
    if (tiles.length <= limit) return { z, tiles };
  }
  const tiles = tilesForBbox(bbox, minZ);
  return { z: minZ, tiles: tiles.slice(0, limit) };
}

/** 解析单块 MVT → GeoJSON features（WGS84），再按当前底图适配器纠偏 */
export function mvtBufferToGcjFeatures(buffer, z, x, y, sourceLayer) {
  if (!buffer) return [];
  const tile = new VectorTile(new Pbf(buffer));
  const layerName = sourceLayer && tile.layers[sourceLayer]
    ? sourceLayer
    : Object.keys(tile.layers || {})[0];
  const layer = layerName ? tile.layers[layerName] : null;
  if (!layer) return [];
  const features = [];
  for (let i = 0; i < layer.length; i += 1) {
    const f = layer.feature(i);
    const geom = f.toGeoJSON(x, y, z);
    if (!geom?.geometry) continue;
    features.push({
      type: 'Feature',
      properties: { ...(f.properties || {}), ...(geom.properties || {}) },
      geometry: toMapGeometry(geom.geometry)
    });
  }
  return features;
}

/**
 * 拉取框选范围内的 MVT，解码并纠偏为 GCJ GeoJSON FeatureCollection
 */
export async function fetchMapLayerGcjGeoJSON({
  tileUrlTemplate,
  sourceLayer,
  clipBounds,
  minzoom,
  maxzoom,
  mapZoom
}) {
  const bbox = normalizeClipBounds(clipBounds);
  if (!bbox) {
    throw new Error('GCJ 叠加需要框选范围（或图层 bounds）');
  }
  const minZ = Number(minzoom) || 10;
  const maxZ = Number(maxzoom) || 16;
  const mapZ = Number.isFinite(Number(mapZoom)) ? Math.floor(Number(mapZoom)) : 13;
  const preferMax = Math.max(minZ, Math.min(maxZ, mapZ));
  const pack = pickTilesForBbox(bbox, minZ, preferMax, 48);

  const features = [];
  await Promise.all(
    pack.tiles.map(async ({ z, x, y }) => {
      const url = String(tileUrlTemplate)
        .replace('{z}', String(z))
        .replace('{x}', String(x))
        .replace('{y}', String(y));
      try {
        const res = await fetch(url);
        if (!res.ok) return;
        const buf = await res.arrayBuffer();
        if (!buf || buf.byteLength < 2) return;
        features.push(
          ...mvtBufferToGcjFeatures(new Uint8Array(buf), z, x, y, sourceLayer)
        );
      } catch (_) {
        /* 单瓦片失败忽略 */
      }
    })
  );

  return { type: 'FeatureCollection', features };
}
